import { ANSWER_STATUS } from '@constants/quiz';
import { MAX_QUESTIONS, MIN_QUESTIONS } from '@cont/Practice/constants';
import { practiceSessionTypes, savedTypes } from '../constants';

const initialState = {
  selectedQuestionCount: 10,
  isSkipped: false,
  hasSubmitted: false,
  isSubmitting: false,
  questionSaved: false,
  hintSeen: false,
  submittedFeedbackType: null,
  isLastQuestion: false,
  topologies: {},
  levelDetails: {},
  currentSession: {},
  currentQuestion: {},
  currentQuestionResult: {},
  sessionReport: {},
  nextQuestion: {},
  selectedTopic: {},
  selectedAnswer: [],
  questions: {
    right: {},
    wrong: {},
    skipped: {}
  }
};

const reducerMap = {
  [`${practiceSessionTypes.FETCH_PRACTICE_TOPOLOGIES}_SUCCESS`](state, action) {
    return {
      ...state,
      topologies: {
        ...state.topologies,
        [action.goalUID]: action.result
      }
    };
  },
  [`${practiceSessionTypes.FETCH_LEARNER_LEVEL_DETAILS}_SUCCESS`](
    state,
    action
  ) {
    const selectedQuestionCount =
      action.result?.streak_threshold >= MIN_QUESTIONS &&
      action.result?.streak_threshold <= MAX_QUESTIONS
        ? action.result?.streak_threshold
        : 10;

    return {
      ...state,
      selectedQuestionCount,
      levelDetails: {
        ...state.levelDetails,
        [action.goalUID]: action.result
      }
    };
  },
  [`${practiceSessionTypes.FETCH_PRACTICE_SESSION}_SUCCESS`](state, action) {
    const { latest_session_attempts = [], ...rest } = action.result;
    const questions = {
      right: {},
      wrong: {},
      skipped: {}
    };

    latest_session_attempts.forEach(({ status, rank }) => {
      if (status === ANSWER_STATUS.CORRECT_ATTEMPT) {
        questions.right[rank] = true;
      } else if (status === ANSWER_STATUS.INCORRECT_ATTEMPT) {
        questions.wrong[rank] = true;
      } else if (status === ANSWER_STATUS.SKIPPED) {
        questions.skipped[rank] = true;
      }
    });

    return {
      ...state,
      questions,
      currentSession: rest
    };
  },
  [`${practiceSessionTypes.GET_NEXT_PRACTICE_QUESTION}_SUCCESS`](
    state,
    action
  ) {
    return {
      ...state,
      isLastQuestion: action.result.is_last_question,
      questionSaved: action.result.question.is_saved,
      currentQuestion: action.result.question
    };
  },
  [`${practiceSessionTypes.SUBMIT_PRACTICE_SESSION_ANSWER}_REQUEST`](state) {
    return {
      ...state,
      isSubmitting: true
    };
  },
  [`${practiceSessionTypes.SUBMIT_PRACTICE_SESSION_ANSWER}_SUCCESS`](
    state,
    action
  ) {
    return {
      ...state,
      isSkipped: action.isSkipped,
      isSubmitting: false,
      hasSubmitted: true,
      nextQuestion: action.result.next_question,
      currentQuestionResult: action.result.present_question_data,
      sessionReport: action.result.next_question.session_report,
      questions: {
        right: {
          ...state.questions.right,
          ...(!action.isSkipped &&
            action.result.present_question_data.session_details
              .is_correct_attempt && {
              [state.currentQuestion.question_rank]: true
            })
        },
        wrong: {
          ...state.questions.wrong,
          ...(!action.isSkipped &&
            !action.result.present_question_data.session_details
              .is_correct_attempt && {
              [state.currentQuestion.question_rank]: true
            })
        },
        skipped: {
          ...state.questions.skipped,
          ...(action.isSkipped && {
            [state.currentQuestion.question_rank]: true
          })
        }
      }
    };
  },
  [`${practiceSessionTypes.SUBMIT_PRACTICE_SESSION_ANSWER}_FAILURE`](state) {
    return {
      ...state,
      isSubmitting: false
    };
  },
  [`${practiceSessionTypes.SETUP_NEXT_PRACTICE_QUESTION}`](state) {
    return {
      ...state,
      isSkipped: false,
      isSubmitting: false,
      hasSubmitted: false,
      isLastQuestion: state.nextQuestion.is_last_question,
      questionSaved: state.nextQuestion.question.is_saved,
      hintSeen: false,
      submittedFeedbackType: null,
      currentQuestion: state.nextQuestion.question,
      selectedAnswer: [],
      nextQuestion: {},
      currentQuestionResult: {},
      sessionReport: {}
    };
  },
  [`${practiceSessionTypes.FINISH_PRACTICE_SESSION}_SUCCESS`](state, action) {
    return {
      ...state,
      sessionReport: action.result.session_report
    };
  },
  [`${practiceSessionTypes.SET_SELECTED_PRACTICE}`](state, action) {
    return {
      ...state,
      selectedTopic: action.data
    };
  },
  [`${practiceSessionTypes.SET_PRACTICE_ANSWERS}`](state, action) {
    return {
      ...state,
      selectedAnswer: action.data
    };
  },
  [`${practiceSessionTypes.SET_PRACTICE_SEEN_HINT}`](state) {
    return {
      ...state,
      hintSeen: true
    };
  },
  [`${practiceSessionTypes.FINISH_PRACTICE_QUESTION_REVIEW}_SUCCESS`](
    state,
    action
  ) {
    return {
      ...state,
      submittedFeedbackType: action.review
    };
  },
  [`${savedTypes.REMOVE_SAVED_ENTITY}_SUCCESS`](state, action) {
    const { objectUid } = action;
    if (!objectUid) return state;

    return {
      ...state,
      questionSaved: false
    };
  },
  [`${savedTypes.SAVE_ENTITY}_SUCCESS`](state, action) {
    const { objectUid } = action;
    if (!objectUid) return state;

    return {
      ...state,
      questionSaved: true
    };
  },
  [`${practiceSessionTypes.RESET_PRACTICE_SESSION}`](state) {
    return {
      ...initialState,
      selectedQuestionCount: state.selectedQuestionCount,
      topologies: {
        ...state.topologies
      },
      levelDetails: {
        ...state.levelDetails
      }
    };
  },
  [`${practiceSessionTypes.SET_PRACTICE_QUESTION_COUNT}`](state, action) {
    return {
      ...state,
      selectedQuestionCount: action.data
    };
  }
};

const reducer = (state = initialState, action) => {
  const actionHandler = reducerMap[action.type];
  return actionHandler ? actionHandler(state, action) : state;
};

export default reducer;
